import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import * as Yup from "yup";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import {makeRequired, makeValidate} from "mui-rff";
import useIsMounted from "../../../hooks/use-is-mounted";
import Api from "../../../../core/services/api/api";
import {Col, Row} from "reactstrap";
import Form from "../../base/form";
import Button from "../../base/button/button";
import {OutlinedInputForm} from "../../base/input/outlined_input";
import {OutlinedDropdown, OutlinedDropdownForm} from "../../base/dropdown/outlined_dropdown";
import {Checkbox, CheckboxForm} from "../../base/checkbox/checkbox";
import classnames from "classnames";
import {ProductTypes} from "../../../../core/constants/enums";
import useIndustry from "../../../hooks/use-industry";
import {selectedCategoryHook} from "../../../hooks/use-selected-category";
import {routes} from "../../../../core/models/static/routes/app-routes";
import {Link} from "react-router-dom";
import LoadingLottieAnimation from "../../app-specific/lottie-animations/loading";
import {OpenInNew, WarningOutlined} from "@mui/icons-material";
import {IconButton} from "@mui/material";
import {useTranslation} from "react-i18next";


const CreateProductForm = ({initialValues: initialValuesProps, onProductCreated, className, industryReadonly = false, terminate}) => {
    const {t} = useTranslation('components', {keyPrefix: 'app-specific.create-product-form'});
    const schema = useMemo(() => Yup.object().shape({
        partNo: Yup.string().trim().nullable().required(ValidateMessages.required),
        type: Yup.number().nullable().required(ValidateMessages.required).typeError(ValidateMessages.incorrectType("Part Type")),
        industry: Yup.object().nullable().required(ValidateMessages.required),
        category: Yup.number().nullable().required(ValidateMessages.required),
        warehouses: Yup.array().nullable(),
        isVisible: Yup.boolean().default(true),
    }), [])

    const validate = useMemo(() => makeValidate(schema), [schema]);
    const required = useMemo(() => makeRequired(schema), [schema]);

    const [submitting, setSubmitting] = useState(false);
    const [warehouses, setWarehouses] = useState([]);
    const [types, setTypes] = useState([...Object.values(ProductTypes).map((e, i) => ({name: e, id: i + 1}))]);
    const isMounted = useIsMounted();

    const {
        industries,
        cachedIndustry,
        fetchIndustry,
        fetchedIndustry,
        categoriesOfFetchedIndustry: categories,
        hasNoIndustries,
        loadingIndustries
    } = useIndustry();

    const [initialValues, setInitialValues] = useState({isVisible: true});
    const setInitialCachedIndustry = useRef(false);

    /**
     * Set the initial values of the form when the dialog opens.
     */
    useEffect(() => {
        if (!industries.length || !cachedIndustry?.id || setInitialCachedIndustry.current)
            return;
        setInitialCachedIndustry.current = true;
        setInitialValues(prevState => {
            let newIndustry = prevState.industry;
            if (!!cachedIndustry?.id) {
                newIndustry = industries?.find(e => e.id === cachedIndustry?.id);
            }
            if (initialValuesProps?.industry?.id) {
                newIndustry = industries?.find(e => e.id === initialValuesProps?.industry?.id);
            }
            if (newIndustry) {
                void fetchIndustry({industryId: newIndustry.id});
            }
            return {
                ...prevState,
                partNo: initialValuesProps?.partNo ?? '',
                type: initialValuesProps?.type ?? null,
                industry: newIndustry,
                category: initialValuesProps?.category ?? null,
                warehouses: [],
                isVisible: prevState.isVisible ?? true,
            };
        })
        setInitialCachedIndustry.current = true;
    }, [cachedIndustry?.id, fetchIndustry, industries, initialValuesProps]);

    /**
     * Set the initial values of the form when the dialog opens.
     */
    useEffect(() => {
        if (!industries.length || !fetchedIndustry?.id)
            return;
        setInitialValues(prevState => ({
            ...prevState,
            industry: industries?.find(e => e.id === fetchedIndustry?.id),
        }))
    }, [industries, fetchedIndustry?.id]);


    /**
     * Fetches the warehouses that the partner has access to  from the server and sets them in the internal state.
     */
    const getWarehouses = useCallback(async () => {
        const response = await Api.getOperatorWarehouses();
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setWarehouses(response?.data ?? []);
            setInitialValues(prevState => ({
                ...prevState,
                warehouses: response?.data?.map(e => e.id) ?? []
            }))
        }
    }, [isMounted])


    /**
     * Fetches the categories of the system from the server and sets them in the internal state.
     */
    const getProductTypes = useCallback(async () => {
        const response = await Api.getProductTypes();
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setTypes(response?.data ?? []);
        }
    }, [isMounted])


    /**
     * As soon as the component mounts:
     * - fetches the system categories from the server.
     * - fetches the partner warehouses from the server.
     * - fetches the product types from the server.
     */
    useEffect(() => {
        getWarehouses().then();
        getProductTypes().then();
    }, [getWarehouses, getProductTypes]);

    /**
     * Registers the warehouses that the partner has access to for the product that is being created.
     * @param productId             The id of the product that is being created.
     * @param warehouseIds          The ids of the warehouses that are selected for the product.
     * @returns {Promise<void>}
     */
    const registerWarehouses = useCallback(async (productId, warehouseIds) => {
        let selectedWarehouses = warehouses.filter(e => warehouseIds.includes(e.id));
        const mainWarehouse = selectedWarehouses.find(e => e.isMain) ?? selectedWarehouses.find(e => e.code === '00');
        if (mainWarehouse) {
            selectedWarehouses = selectedWarehouses.filter(e => e.id !== mainWarehouse?.id);
            await Api.createPartInWarehouse({partId: productId, warehouseId: mainWarehouse?.id});
        }
        const promises = selectedWarehouses.map(e => Api.createPartInWarehouse({partId: productId, warehouseId: e.id}));
        await Promise.allSettled(promises);
    }, [warehouses])

    /**
     * Uses the filters to redirect the user to basic search result
     * @param values {any} values form the form
     */
    const createNewPart = useCallback(async (values) => {

        const forApi = {
            partNumber: values.partNo?.trim(),
            categoryId: values.category,
            partTypeId: values.type,
            industryId: values.industry?.id,
            isVisible: values.isVisible ?? true,
        }
        const errors = {};
        if (categories.some(e => e.parentId === forApi.categoryId)) {
            errors['category'] = ValidateMessages.required
        }
        if (Object.keys(errors).length)
            return errors;
        setSubmitting(true);
        const response = await Api.createProductV2(forApi);
        if (!isMounted())
            return;
        setSubmitting(false);
        if (response?.resultFlag) {
            void registerWarehouses(response.data.id, values.warehouses);
            onProductCreated(response.data);
        }
    }, [categories, isMounted, onProductCreated, registerWarehouses]);

    /**
     * Empties the sub-category values of a particular category that has been changed.
     * @param e
     * @param form
     */
    const onIndustryChange = useCallback((e, form) => {
        form.change('category', null);
        fetchIndustry({industryId: e.target.value.id}).then();
    }, [fetchIndustry])

    /**
     * Selects or unselects all warehouses for the part that is being created.
     * @param {formApi} form
     * @param {boolean} checked
     */
    const toggleAllWarehouseSelection = useCallback((form, checked) => {
        if (checked) {
            form.change('warehouses', warehouses.map(e => e.id));
        } else {
            form.change('warehouses', []);
        }
    }, [warehouses])

    /**
     * Selects or unselects the value for the given warehouse.
     * @param {formApi} form
     * @param {number} warehouseId
     * @param {boolean} checked
     * @param {Array<number>} selectedWarehouses
     */
    const toggleWarehouseSelection = useCallback((form, warehouseId, checked, selectedWarehouses) => {
        if (!checked) {
            form.change('warehouses', selectedWarehouses?.filter(e => e !== warehouseId) ?? []);
        } else {
            form.change('warehouses', [...(selectedWarehouses ?? []), warehouseId]);
        }
    }, [])

    return (
        <>
            {
                loadingIndustries &&
                <LoadingLottieAnimation width={'80%'}/>
            }
            {
                hasNoIndustries && !loadingIndustries &&
                <p className={'text-sm text-color-3 font-weight-semi-bold text-center my-5'}>
                    There are no industries to create a product for. Please contact the system administrator.
                </p>
            }
            {
                !hasNoIndustries && !loadingIndustries &&
                <Form
                    onSubmit={createNewPart}
                    validate={validate}
                    className={className}
                    initialValues={initialValues}
                    render={({values, form}) => {
                        const {
                            getSubCategories,
                            getSelectedCategoriesHierarchy
                        } = selectedCategoryHook(categories, values.category);
                        const selectedCategoriesHierarchy = getSelectedCategoriesHierarchy();
                        const subCategories = getSubCategories();
                        return (
                            <Row className='no-gutters justify-content-start pt-2'>
                                <Col xs={12} className='px-2 mb-3'>
                                    <OutlinedInputForm
                                        onChange={(e) => {
                                            const partNo = e.target?.value?.toUpperCase();
                                            const cursorPosition = e.target?.selectionStart;
                                            e.target.value = partNo;
                                            e.target.setSelectionRange(cursorPosition ?? null, cursorPosition ?? null);
                                            form.change(e.target.name, partNo)
                                        }}
                                        value={values?.partNo ?? ''}
                                        label={'Part Number'}
                                        name={'partNo'}
                                        type={'text'}
                                        required={required.partNo}
                                        placeholder={'part number...'}
                                    />
                                </Col>
                                <Col xs={12} className='px-2 mb-3'>
                                    <OutlinedDropdownForm
                                        label={'Part Type'}
                                        name={'type'}
                                        renderValue={(v) => types.find(e => e.id === v)?.name ?? ''}
                                        required={required.type}
                                        data={types?.map(e => ({
                                            label: e.name,
                                            value: e.id,
                                            id: e.id,
                                        })) ?? []}
                                    />
                                </Col>
                                {
                                    industries.length > 1 &&
                                    <Col xs={12} className='px-2 mb-3'>
                                        <OutlinedDropdownForm
                                            label={'Industry'}
                                            name={'industry'}
                                            disabled={industryReadonly || submitting}
                                            required={required.industry}
                                            data={industries?.map(e => ({value: e, label: e.title, id: e.id})) ?? []}
                                            onChange={(e) => onIndustryChange(e, form)}
                                            SelectProps={{
                                                renderSelectedValue: (e) => e.title,
                                            }}
                                        />
                                    </Col>
                                }
                                {
                                    selectedCategoriesHierarchy.map((category, index) => (
                                        <Col xs={12} key={`category-${category.id}`} className='px-2 mb-3'>
                                            <OutlinedDropdown
                                                fullWidth
                                                label={index === 0 ? `Category` : "Sub category"}
                                                data={category.options?.map(e => ({label: e.title, value: e.id, id: e.id})) ?? []}
                                                disabled={submitting}
                                                onChange={(categoryId) => form.change('category', categoryId)}
                                                value={category.id}
                                                SelectProps={{
                                                    renderSelectedValue: () => category.title,
                                                }}
                                            />
                                        </Col>
                                    ))
                                }
                                {
                                    subCategories.length > 0 &&
                                    <Col xs={12} className='px-2 mb-3'>
                                        <OutlinedDropdownForm
                                            fullWidth
                                            label={selectedCategoriesHierarchy.length ? "Sub category" : "Category"}
                                            name={`category`}
                                            required={required.category}
                                            disabled={submitting}
                                            data={subCategories?.map(e => ({label: e.title, value: e.id, id: e.id,})) ?? []}
                                            onChange={(e) => form.change('category', e.target.value)}
                                            value={selectedCategoriesHierarchy[selectedCategoriesHierarchy.length - 1]?.id ?? ""}
                                            renderValue={(e) => subCategories.find(c => c.id === e)?.title}
                                        />
                                    </Col>
                                }
                                <Col xs={12} className='px-2 mb-3'>
                                    <CheckboxForm
                                        name={'isVisible'}
                                        disabled={submitting}
                                        data={{
                                            value: values?.isVisible,
                                            label: t('isVisible'),
                                        }}
                                    />
                                </Col>
                                <Col xs={12} className='px-2 mb-3 d-flex flex-column'>
                                    <div className={'d-flex p-2 px-5 background-secondary-color-4 rounded-xl' +
                                        ' justify-content-between align-items-center'}>
                                        <p className={'text-xs text-color-3 font-weight-semi-bold'}>
                                            Warehouses
                                        </p>
                                        <div className={'d-flex align-items-center'}>
                                            <p className={'text-xs text-color-3 font-weight-semi-bold'}>
                                                Select All
                                            </p>
                                            <Checkbox
                                                checked={values?.warehouses?.length === warehouses.length}
                                                onChange={() => toggleAllWarehouseSelection(form, values?.warehouses?.length !== warehouses.length)}
                                                labelClassName={'m-0'}
                                                disabled={submitting}
                                            />
                                        </div>
                                    </div>
                                    {
                                        warehouses.map((warehouse) => (
                                            <div key={warehouse.id} className={classnames(
                                                'd-flex p-2 px-5 justify-content-between align-items-center',
                                            )}>
                                                <p className={'text-sm text-color-1'}>
                                                    {warehouse.name ?? '--'} ({warehouse.code ?? '--'})
                                                </p>
                                                <Checkbox
                                                    disabled={submitting}
                                                    checked={values.warehouses?.includes(warehouse.id) ?? false}
                                                    onChange={(e) => toggleWarehouseSelection(form, warehouse.id, e.target.checked, values.warehouses)}
                                                    labelClassName={'m-0'}
                                                />
                                            </div>
                                        ))
                                    }
                                </Col>
                                {
                                    subCategories.length === 0 && selectedCategoriesHierarchy.length === 0 &&
                                    <Col xs={12} className='px-2 mb-3'>
                                        <p className={'text-sm text-color-3 text-center'}>
                                            <WarningOutlined
                                                color={'warning'}
                                                sx={{display: 'inline-block', marginInlineEnd: 0.5}}
                                            />
                                            There are no categories to select. Please create a category first.
                                            <IconButton
                                                color={'paper.primary'}
                                                component={Link}
                                                to={routes.products.categoryManagement}
                                                target={'_blank'}
                                                sx={{display: 'inline-block', marginInlineStart: 0.5}}
                                                onClick={() => terminate?.()}

                                            >
                                                <OpenInNew/>
                                            </IconButton>
                                        </p>
                                    </Col>
                                }
                                <Col
                                    xs={12}
                                    className='px-3 mt-2 align-items-center row no-gutters justify-content-center align-items-center'
                                >
                                    <Button
                                        disabled={!!submitting || (subCategories.length === 0 && selectedCategoriesHierarchy.length === 0)}
                                        type={'submit'}
                                        onClick={() => form.submit()}
                                        variant={'contained'}
                                        color={'primary'}
                                        size={'medium'}
                                        sx={{minWidth: '200px'}}
                                    >
                                        {
                                            submitting
                                                ? "Creating..."
                                                : "Create"
                                        }
                                    </Button>
                                </Col>
                            </Row>
                        );
                    }}
                />
            }
        </>
    )
}

export default CreateProductForm;
